class StickyAddToCart extends HTMLElement {
    constructor() {
        super();

        this.layoutType = this.querySelector('sticky-add-to-cart-content')?.getAttribute('layout-type') || '';
        this._boundUpdateContent = this._updateContent.bind(this);
        this._boundUnavailable = this._updateUnavailable.bind(this);
        this._refreshElements();

        document.addEventListener(_EVENT_HELPER.updateStickyAddToCartContent, this._boundUpdateContent);
        document.addEventListener(_EVENT_HELPER.updateStickyAddToCartContentUnAvailable, this._boundUnavailable);
    }

    _refreshElements() {
        this.variantList = this.querySelector('[sticky-add-to-cart-variants]');
        this.addToCartButton = this.querySelector('[add-to-cart-button]');
    }

    _updateContent(event) {
        const contentList = event.detail?.sticky_content || [];
        const target = this.querySelector('[sticky-add-to-cart-content]');
        if (!target) return;

        for (const element of contentList) {
            const content = element.querySelector('sticky-add-to-cart-content');
            if (content?.getAttribute('layout-type') === this.layoutType) {
                target.innerHTML = element.innerHTML;
                this._refreshElements();
                break;
            }
        }
    }

    _updateUnavailable(event) {
        this._refreshElements();
        if (this.variantList && Array.isArray(event.detail?.variantOptions)) {
            this.variantList.textContent = event.detail.variantOptions.join(', ');
        }
        this.toggleAddToCartButton(false);
    }

    toggleAddToCartButton(buttonEnabled) {
        if (!this.addToCartButton) return;

        if (buttonEnabled) {
            this.addToCartButton.removeAttribute('disabled');
            return;
        }

        if (!this.addToCartButton.classList.contains('sticky-icon-button')) {
            this.addToCartButton.textContent = window.variantStrings.unavailable;
        }
        this.addToCartButton.removeAttribute('form');
        this.addToCartButton.setAttribute('disabled', '');
    }

    disconnectedCallback() {
        document.removeEventListener(_EVENT_HELPER.updateStickyAddToCartContent, this._boundUpdateContent);
        document.removeEventListener(_EVENT_HELPER.updateStickyAddToCartContentUnAvailable, this._boundUnavailable);
    }
}

if (!window.customElements.get('sticky-add-to-cart')) {
    window.customElements.define('sticky-add-to-cart', StickyAddToCart);
}

class StickyAddToCartContent extends HTMLElement {
    constructor() {
        super();

        this.breakpoint = window.matchMedia('(min-width:768px)');
        this.layoutType = this.getAttribute('layout-type') || '';
        this._listenersAttached = false;

        this._boundAddToCart = this._handleAddToCart.bind(this);
        this._boundReset = this._handleReset.bind(this);
        this._boundScroll = this._scrollHandler.bind(this);
        this._boundResize = this.screenResize.bind(this);
        this._boundVariantError = this._handleVariantError.bind(this);
        this._boundScrollToSection = this._scrollToSection.bind(this);
        this._resizeObserver = null;
    }

    _refreshReferences() {
        this.addToCartButton = this.querySelector('[add-to-cart-button]');
        this._productContainer = document.querySelector('[product-container]');
        this._scrollToSectionButton = this.querySelector('[scroll-to-section]');
        this._drawer = this.closest('drawer-component');
        this.drawerType = this._drawer?.getAttribute('drawer-type') || 'sticky-add-to-cart';
        this.scrollToSection = this._scrollToSectionButton?.getAttribute('scroll-to-section') || '';
        this.scrollToSectionElement = this.scrollToSection
            ? document.querySelector(`#shopify-section-${CSS.escape(this.scrollToSection)} [variant-block]`)
            : null;
    }

    _attachListeners() {
        if (this._listenersAttached) return;
        this.addToCartButton?.addEventListener('click', this._boundAddToCart);
        this._scrollToSectionButton?.addEventListener('click', this._boundScrollToSection);
        document.addEventListener(_EVENT_HELPER.resetStickyAddTocartButton, this._boundReset);
        if (this.layoutType !== 'drawer') {
            document.addEventListener('scroll', this._boundScroll, { passive: true });
        }
        window.addEventListener('resize', this._boundResize);
        document.addEventListener(_EVENT_HELPER.triggerVariantAddToCartError, this._boundVariantError);
        this._listenersAttached = true;
    }

    connectedCallback() {
        this._refreshReferences();
        this._attachListeners();
        if (!this._productContainer) {
            this._setBarVisibility(false);
            return;
        }
        if ('ResizeObserver' in window) {
            this._resizeObserver = new ResizeObserver(this._boundResize);
            this._resizeObserver.observe(this._productContainer);
        }
        this.screenResize();
    }

    _handleAddToCart() {
        if (!this.addToCartButton) return;
        const formId = this.addToCartButton.getAttribute('form');
        const form = formId ? document.getElementById(formId) : null;
        if (!form) return;

        if (!form.checkValidity()) {
            HELPER_UTIL.dispatchCustomEvent(_EVENT_HELPER.hideDrawer, this, {
                drawerType: this.drawerType,
            });
            setTimeout(() => {
                const productForm = form.closest('product-form');
                productForm?.scrollToFirstInvalidField?.();
            }, 400);
            return;
        }

        HELPER_UTIL.submitButtonState(this.addToCartButton).loadingState();
        HELPER_UTIL.dispatchCustomEvent('submit', form);
    }

    _handleReset() {
        if (!this.addToCartButton) return;
        const buttonState = HELPER_UTIL.submitButtonState(this.addToCartButton);
        buttonState.resetState(1000);
        buttonState.disableState(false);
    }

    _handleVariantError(event) {
        const outer = this.closest('sticky-add-to-cart');
        const drawerIsOpen = this._drawer?.getAttribute('aria-hidden') === 'false';
        const barIsVisible = outer?.classList.contains('active');
        if (drawerIsOpen || barIsVisible) this._scrollToSection(event);
    }

    _scrollToSection(event) {
        event?.preventDefault?.();
        HELPER_UTIL.dispatchCustomEvent(_EVENT_HELPER.hideDrawer, this, {
            drawerType: this.drawerType,
        });
        if (this.scrollToSectionElementOffset > 0) {
            window.scrollTo({
                top: this.scrollToSectionElementOffset,
                behavior: 'smooth',
            });
        }
    }

    screenResize() {
        this._updateProductMetrics();

        if (!this.scrollToSectionElement || !this._productContainer) {
            this.scrollToSectionElementOffset = 0;
        } else if (this.breakpoint.matches) {
            this.scrollToSectionElementOffset = this.scrollToSectionElement.offsetTop + this._productContainer.offsetTop;
        } else {
            const mobileProductInfo = document.querySelector('[scroll-product-info]');
            this.scrollToSectionElementOffset = (mobileProductInfo?.offsetTop || 0) + this.scrollToSectionElement.offsetTop;
        }

        if (this.layoutType !== 'drawer') this._scrollHandler();
    }

    _updateProductMetrics() {
        if (!this._productContainer) {
            this._totalScrollHeight = Number.NaN;
            return;
        }
        const productBounds = this._productContainer.getBoundingClientRect();
        this._productContainerOffsetTop = productBounds.top + window.scrollY;
        this._productContainerHeight = productBounds.height;
        this._totalScrollHeight = this._productContainerOffsetTop + this._productContainerHeight;
    }

    _setBarVisibility(isActive) {
        if (this.layoutType === 'drawer') return;
        const outer = this.closest('sticky-add-to-cart');
        if (!outer) return;
        outer.classList.toggle('active', isActive);
        outer.toggleAttribute('inert', !isActive);
        outer.setAttribute('aria-hidden', String(!isActive));
    }

    _scrollHandler() {
        if (this.layoutType === 'drawer') return;
        if (!Number.isFinite(this._totalScrollHeight)) return;
        const outer = this.closest('sticky-add-to-cart');
        const isRendered = outer && window.getComputedStyle(outer).display !== 'none';
        const isActive = Boolean(isRendered && window.scrollY > this._totalScrollHeight);
        this._setBarVisibility(isActive);

        const anyActiveStickyBar = [...document.querySelectorAll('sticky-add-to-cart')]
            .some((element) => element.classList.contains('active'));
        document.body.classList.toggle('sticky-add-to-cart-active', anyActiveStickyBar);
    }

    disconnectedCallback() {
        if (!this._listenersAttached) return;
        this.addToCartButton?.removeEventListener('click', this._boundAddToCart);
        this._scrollToSectionButton?.removeEventListener('click', this._boundScrollToSection);
        document.removeEventListener(_EVENT_HELPER.resetStickyAddTocartButton, this._boundReset);
        if (this.layoutType !== 'drawer') {
            document.removeEventListener('scroll', this._boundScroll);
        }
        window.removeEventListener('resize', this._boundResize);
        document.removeEventListener(_EVENT_HELPER.triggerVariantAddToCartError, this._boundVariantError);
        this._resizeObserver?.disconnect();
        this._resizeObserver = null;
        this._listenersAttached = false;
    }
}

if (!window.customElements.get('sticky-add-to-cart-content')) {
    window.customElements.define('sticky-add-to-cart-content', StickyAddToCartContent);
}
